import React, { useState, useEffect } from 'react';
import '../styles/personalProStyle.css';
import authService from '../services/loginService';
import userService from '../services/userService';
import vehicleService from '../services/vehicleService';
import subscriptionUserService from '../services/subscriptionUserService';
import feedbackService from '../services/feedbackService';

// Iconos SVG
const UserIcon = () => (
  <svg className="option-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const CarIcon = () => (
  <svg className="option-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M13 6H4L6 8h7m0-2v8a2 2 0 002 2h2a2 2 0 002-2V9a2 2 0 00-2-2h-1m-1 0V6a2 2 0 00-2-2H9a2 2 0 00-2 2v2h6z" />
  </svg>
);

const SubscriptionIcon = () => (
  <svg className="option-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const FormIcon = () => (
  <svg className="option-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
  </svg>
);

const ProfileScreen = () => {
  const [activeTab, setActiveTab] = useState('personal');
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userVehicles, setUserVehicles] = useState([]);
  const [userSubscription, setUserSubscription] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    lastname: '',
    email: '',
    idCard: '',
    typeID: ''
  });

  const [carData, setCarData] = useState({
    plate: '',
    brand: '',
    model: '',
    type: 'Carro'
  });

  const [editingCar, setEditingCar] = useState(null);
  const [vehicleDetails, setVehicleDetails] = useState({});

  const [feedbackData, setFeedbackData] = useState({
    nombre: '',
    email: '',
    tipo: 'Sugerencias',
    comentario: ''
  });

  // Cargar datos del usuario al montar el componente
  useEffect(() => {
    loadUserData();
  }, []);

  // Cargar datos específicos según la pestaña activa
  useEffect(() => {
    if (currentUser) {
      switch (activeTab) {
        case 'cars':
          loadUserVehicles();
          break;
        case 'subscription':
          loadUserSubscription();
          break;
        case 'feedback':
          // Prellenar email del usuario en feedback
          setFeedbackData(prev => ({
            ...prev,
            email: currentUser.email,
            nombre: `${currentUser.name} ${currentUser.lastname}`
          }));
          break;
        default:
          break;
      }
    }
  }, [activeTab, currentUser]);

  const loadUserData = async () => {
    try {
      const user = authService.getCurrentUser();
      if (user) {
        setCurrentUser(user);
        setFormData({
          name: user.name || '',
          lastname: user.lastname || '',
          email: user.email || '',
          idCard: user.idCard || '',
          typeID: user.typeID || ''
        });
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      alert('Error al cargar datos del usuario');
    }
  };

  const loadUserVehicles = async () => {
    if (!currentUser?.idCard) return;
    
    try {
      setLoading(true);
      const response = await vehicleService.getUserVehicles(currentUser.idCard);
      const plates = response.vehicles || [];
      setUserVehicles(plates);
      
      // Cargar detalles de cada vehículo
      const details = {};
      for (const plate of plates) {
        try {
          // Note: Necesitarías un endpoint para obtener detalles por placa
          // Por ahora solo tenemos las placas
          details[plate] = { plate };
        } catch (error) {
          console.error(`Error loading details for vehicle ${plate}:`, error);
          details[plate] = { plate };
        }
      }
      setVehicleDetails(details);
    } catch (error) {
      console.error('Error loading vehicles:', error);
      alert('Error al cargar vehículos');
    } finally {
      setLoading(false);
    }
  };

  const loadUserSubscription = async () => {
    if (!currentUser?.idCard) return;
    
    try {
      setLoading(true);
      const response = await subscriptionUserService.getActiveSubscription(currentUser.idCard);
      setUserSubscription(response);
    } catch (error) {
      console.error('Error loading subscription:', error);
      setUserSubscription(null);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (section, e) => {
    const { name, value } = e.target;
    
    switch(section) {
      case 'personal':
        setFormData(prev => ({ ...prev, [name]: value }));
        break;
      case 'car':
        setCarData(prev => ({ ...prev, [name]: value }));
        break;
      case 'feedback':
        setFeedbackData(prev => ({ ...prev, [name]: value }));
        break;
    }
  };

  const handleEdit = async () => {
    if (!currentUser?.idCard) {
      alert('Error: No se encontró información del usuario');
      return;
    }

    // Validar campos requeridos
    if (!formData.name || !formData.lastname || !formData.email) {
      alert('Por favor complete todos los campos obligatorios (Nombre, Apellido, Email)');
      return;
    }

    // Validar formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      alert('Por favor ingrese un email válido');
      return;
    }

    try {
      setLoading(true);
      
      // Preparar datos completos para el backend
      const updateData = {
        idCard: formData.idCard,
        email: formData.email.trim(),
        name: formData.name.trim(),
        lastname: formData.lastname.trim(),
        typeID: formData.typeID,
        role: currentUser.role // Mantener el role actual
      };
      
      console.log('Updating user with data:', updateData);
      console.log('Current user idCard:', currentUser.idCard);
      
      // El backend espera idCard como parámetro, no _id
      const response = await userService.updateUser(currentUser.idCard, updateData);
      console.log('Update response:', response);
      
      // Actualizar datos en sessionStorage
      const updatedUser = { ...currentUser, ...updateData };
      sessionStorage.setItem('user', JSON.stringify(updatedUser));
      setCurrentUser(updatedUser);
      
      alert('Información personal actualizada exitosamente');
    } catch (error) {
      console.error('Error updating user:', error);
      console.error('Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      
      const errorMessage = error.response?.data?.message || 'Error al actualizar información personal';
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!currentUser?.idCard) return;
    
    const confirmDelete = window.confirm('¿Está seguro que desea eliminar su cuenta? Esta acción no se puede deshacer.');
    if (!confirmDelete) return;

    try {
      setLoading(true);
      // El backend espera idCard para eliminar
      await userService.deleteUser(currentUser.idCard);
      alert('Cuenta eliminada exitosamente');
      authService.logout();
      window.location.href = '/';
    } catch (error) {
      console.error('Error deleting account:', error);
      const errorMessage = error.response?.data?.message || 'Error al eliminar cuenta';
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCarAction = async (action) => {
    if (!currentUser?.idCard) return;

    if (action === 'add') {
      if (!carData.plate || !carData.brand || !carData.model) {
        alert('Por favor complete todos los campos del vehículo');
        return;
      }

      try {
        setLoading(true);
        await vehicleService.addVehicle({
          idCard: currentUser.idCard,
          ...carData
        });
        
        setCarData({ plate: '', brand: '', model: '', type: 'Carro' });
        await loadUserVehicles();
        alert('Vehículo agregado exitosamente');
      } catch (error) {
        console.error('Error adding vehicle:', error);
        const errorMessage = error.response?.data?.message || 'Error al agregar vehículo';
        alert(errorMessage);
      } finally {
        setLoading(false);
      }
    } else if (action === 'edit' && editingCar) {
      try {
        setLoading(true);
        await vehicleService.updateVehicle(editingCar, {
          brand: carData.brand,
          model: carData.model,
          type: carData.type
        });
        setEditingCar(null);
        setCarData({ plate: '', brand: '', model: '', type: 'Carro' });
        await loadUserVehicles();
        alert('Vehículo actualizado exitosamente');
      } catch (error) {
        console.error('Error updating vehicle:', error);
        alert('Error al actualizar vehículo');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDeleteVehicle = async (plate) => {
    if (!currentUser?.idCard) return;
    
    const confirmDelete = window.confirm(`¿Está seguro que desea eliminar el vehículo con placa ${plate}?`);
    if (!confirmDelete) return;

    try {
      setLoading(true);
      await vehicleService.deleteVehicle(currentUser.idCard, plate);
      await loadUserVehicles();
      alert('Vehículo eliminado exitosamente');
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      alert('Error al eliminar vehículo');
    } finally {
      setLoading(false);
    }
  };

  const handleEditVehicle = (plate) => {
    setEditingCar(plate);
    const vehicleDetail = vehicleDetails[plate] || { plate };
    setCarData({
      plate: vehicleDetail.plate,
      brand: vehicleDetail.brand || '',
      model: vehicleDetail.model || '',
      type: vehicleDetail.type || 'Carro'
    });
  };

  const handleCancelEdit = () => {
    setEditingCar(null);
    setCarData({ plate: '', brand: '', model: '', type: 'Carro' });
  };

  const handleSubscriptionAction = async (action) => {
    if (action === 'cancel' && userSubscription?._id) {
      const confirmCancel = window.confirm('¿Está seguro que desea cancelar su suscripción?');
      if (!confirmCancel) return;

      try {
        setLoading(true);
        await subscriptionUserService.cancelSubscription(userSubscription._id);
        await loadUserSubscription();
        alert('Suscripción cancelada exitosamente');
      } catch (error) {
        console.error('Error canceling subscription:', error);
        alert('Error al cancelar suscripción');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleFeedbackSubmit = async () => {
    if (!feedbackData.email || !feedbackData.tipo || !feedbackData.comentario) {
      alert('Por favor complete todos los campos obligatorios');
      return;
    }

    try {
      setLoading(true);
      await feedbackService.submitFeedback(feedbackData);
      
      setFeedbackData(prev => ({
        ...prev,
        comentario: '',
        tipo: 'Sugerencias'
      }));
      
      alert('Formulario enviado exitosamente');
    } catch (error) {
      console.error('Error submitting feedback:', error);
      const errorMessage = error.response?.data?.message || 'Error al enviar formulario';
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const renderPersonalInfo = () => (
    <>
      <h2>Tu información en esta cuenta</h2>
      
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Nombre</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={(e) => handleInputChange('personal', e)}
            className="form-input"
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Apellido</label>
          <input
            type="text"
            name="lastname"
            value={formData.lastname}
            onChange={(e) => handleInputChange('personal', e)}
            className="form-input"
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">E-mail</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={(e) => handleInputChange('personal', e)}
            className="form-input"
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Cédula</label>
          <input
            type="text"
            name="idCard"
            value={formData.idCard}
            onChange={(e) => handleInputChange('personal', e)}
            className="form-input"
            disabled
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Tipo de cédula</label>
          <select
            name="typeID"
            value={formData.typeID}
            onChange={(e) => handleInputChange('personal', e)}
            className="form-select"
          >
            <option value="">Seleccionar...</option>
            <option value="Nacional">Nacional</option>
            <option value="Extranjero">Extranjero</option>
            <option value="Juridica">Jurídica</option>
          </select>
        </div>
      </div>

      <div className="action-buttons">
        <button 
          className="btn btn-primary" 
          onClick={handleEdit}
          disabled={loading}
        >
          {loading ? 'Guardando...' : 'Guardar cambios'}
        </button>
        <button 
          className="btn btn-danger" 
          onClick={handleDeleteAccount}
          disabled={loading}
        >
          Eliminar cuenta
        </button>
      </div>
    </>
  );

  const renderCarRegistration = () => (
    <>
      <h2>Registro de automóviles</h2>
      
      {/* Formulario para agregar/editar vehículo */}
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Placa</label>
          <input
            type="text"
            name="plate"
            value={carData.plate}
            onChange={(e) => handleInputChange('car', e)}
            className="form-input"
            disabled={editingCar}
            placeholder="Ej: ABC123"
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Marca</label>
          <input
            type="text"
            name="brand"
            value={carData.brand}
            onChange={(e) => handleInputChange('car', e)}
            className="form-input"
            placeholder="Ej: Toyota"
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Modelo</label>
          <input
            type="text"
            name="model"
            value={carData.model}
            onChange={(e) => handleInputChange('car', e)}
            className="form-input"
            placeholder="Ej: Corolla"
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Tipo</label>
          <select
            name="type"
            value={carData.type}
            onChange={(e) => handleInputChange('car', e)}
            className="form-select"
          >
            <option value="Carro">Carro</option>
            <option value="Moto">Moto</option>
          </select>
        </div>
      </div>

      <div className="action-buttons">
        {editingCar ? (
          <>
            <button 
              className="btn btn-primary" 
              onClick={() => handleCarAction('edit')}
              disabled={loading}
            >
              {loading ? 'Actualizando...' : 'Actualizar vehículo'}
            </button>
            <button 
              className="btn btn-secondary" 
              onClick={handleCancelEdit}
              disabled={loading}
            >
              Cancelar
            </button>
          </>
        ) : (
          <button 
            className="btn btn-primary" 
            onClick={() => handleCarAction('add')}
            disabled={loading}
          >
            {loading ? 'Agregando...' : 'Agregar vehículo'}
          </button>
        )}
      </div>

      {/* Lista de vehículos registrados */}
      <div className="vehicles-list">
        <h3>Vehículos registrados</h3>
        {loading ? (
          <p>Cargando vehículos...</p>
        ) : userVehicles.length > 0 ? (
          <div className="vehicles-grid">
            {userVehicles.map((plate, index) => {
              const vehicle = vehicleDetails[plate] || { plate };
              return (
                <div key={index} className="vehicle-card">
                  <p><strong>Placa:</strong> {plate}</p>
                  {vehicle.brand && <p><strong>Marca:</strong> {vehicle.brand}</p>}
                  {vehicle.model && <p><strong>Modelo:</strong> {vehicle.model}</p>}
                  {vehicle.type && <p><strong>Tipo:</strong> {vehicle.type}</p>}
                  <div className="vehicle-actions">
                    <button 
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleEditVehicle(plate)}
                      disabled={loading}
                    >
                      Editar
                    </button>
                    <button 
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDeleteVehicle(plate)}
                      disabled={loading}
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p>No hay vehículos registrados</p>
        )}
      </div>
    </>
  );

  const renderSubscription = () => (
    <>
      <h2>Plan de subscripción</h2>
      
      {loading ? (
        <p>Cargando información de suscripción...</p>
      ) : userSubscription ? (
        <div className="subscription-info">
          <div className="subscription-card">
            <h3>Suscripción Activa</h3>
            <p><strong>Monto:</strong> ${userSubscription.amount}</p>
            <p><strong>Fecha de compra:</strong> {new Date(userSubscription.purchaseDate).toLocaleDateString()}</p>
            <p><strong>Fecha de vencimiento:</strong> {new Date(userSubscription.expiration).toLocaleDateString()}</p>
            <p><strong>Estado:</strong> {new Date(userSubscription.expiration) > new Date() ? 'Activo' : 'Vencido'}</p>
          </div>

          <div className="action-buttons">
            <button 
              className="btn btn-danger" 
              onClick={() => handleSubscriptionAction('cancel')}
              disabled={loading}
            >
              {loading ? 'Cancelando...' : 'Cancelar suscripción'}
            </button>
          </div>
        </div>
      ) : (
        <div className="no-subscription">
          <p>No tienes una suscripción activa</p>
          <p>Contacta con soporte para adquirir una suscripción</p>
        </div>
      )}
    </>
  );

  const renderFeedback = () => (
    <>
      <h2>Formulario de quejas/sugerencias</h2>
      
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Nombre completo</label>
          <input
            type="text"
            name="nombre"
            value={feedbackData.nombre}
            onChange={(e) => handleInputChange('feedback', e)}
            className="form-input"
            readOnly
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">E-mail</label>
          <input
            type="email"
            name="email"
            value={feedbackData.email}
            onChange={(e) => handleInputChange('feedback', e)}
            className="form-input"
            readOnly
          />
        </div>
      </div>

      <div className="radio-group" style={{ margin: '20px 0' }}>
        <label className="radio-label">
          <input
            type="radio"
            name="tipo"
            value="Sugerencias"
            checked={feedbackData.tipo === 'Sugerencias'}
            onChange={(e) => handleInputChange('feedback', e)}
          />
          Sugerencias
        </label>
        <label className="radio-label">
          <input
            type="radio"
            name="tipo"
            value="Queja"
            checked={feedbackData.tipo === 'Queja'}
            onChange={(e) => handleInputChange('feedback', e)}
          />
          Queja
        </label>
      </div>

      <div className="form-group">
        <label className="form-label">En este espacio puede dejar su comentario</label>
        <textarea
          name="comentario"
          value={feedbackData.comentario}
          onChange={(e) => handleInputChange('feedback', e)}
          className="form-textarea"
          rows="6"
          placeholder="Escriba su comentario aquí..."
        />
      </div>

      <div className="action-buttons">
        <button 
          className="btn btn-primary" 
          onClick={handleFeedbackSubmit}
          disabled={loading}
        >
          {loading ? 'Enviando...' : 'Enviar'}
        </button>
      </div>
    </>
  );

  const renderContent = () => {
    switch(activeTab) {
      case 'personal':
        return renderPersonalInfo();
      case 'cars':
        return renderCarRegistration();
      case 'subscription':
        return renderSubscription();
      case 'feedback':
        return renderFeedback();
      default:
        return renderPersonalInfo();
    }
  };

  return (
    <div className="profile-container">
      {/* Panel de opciones */}
      <div className="profile-options">
        <h2>Opciones de Perfil</h2>
        
        <div 
          className={`option-item ${activeTab === 'personal' ? 'active' : ''}`}
          onClick={() => setActiveTab('personal')}
        >
          <UserIcon />
          <span className="option-text">Información personal</span>
        </div>
        
        <div 
          className={`option-item ${activeTab === 'cars' ? 'active' : ''}`}
          onClick={() => setActiveTab('cars')}
        >
          <CarIcon />
          <span className="option-text">Registro de automóviles</span>
        </div>
        
        <div 
          className={`option-item ${activeTab === 'subscription' ? 'active' : ''}`}
          onClick={() => setActiveTab('subscription')}
        >
          <SubscriptionIcon />
          <span className="option-text">Plan de subscripción</span>
        </div>
        
        <div 
          className={`option-item ${activeTab === 'feedback' ? 'active' : ''}`}
          onClick={() => setActiveTab('feedback')}
        >
          <FormIcon />
          <span className="option-text">Formulario de quejas/sugerencias</span>
        </div>
      </div>

      {/* Panel de información dinámico */}
      <div className="account-info">
        {renderContent()}
      </div>
    </div>
  );
};

export default ProfileScreen;